/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { memo } from 'preact/compat';
import { useRef, useLayoutEffect, useMemo, StateUpdater } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { mergeRefs } from '../utils/PRIVATE_refUtils';
import { useId } from '../hooks/UNSAFE_useId';
import { useAnimation, UseAnimationConfig } from '../hooks/UNSAFE_useAnimation';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { SelectionMode } from '../UNSAFE_Collection';
import { multiVariantStyles, styles } from './themes/CardGridStyles.css';

export const ITEM_STYLE_CLASS = 'oj-cardgrid-item';
export const ITEM_SELECTOR = '.' + ITEM_STYLE_CLASS;

/**
 * Props for the CardGridItem Component
 */
export type Props<K, D> = {
  children: ComponentChildren;
  itemKey: K;
  itemIndex: number;
  itemData: D;
  isTabbable: boolean;
  isFocused: boolean;
  isFocusRingVisible: boolean;
  isSelected: boolean;
  selectionMode: SelectionMode;
  initialAnimation?: 'slideUp' | 'slideDown';
  setCardDimension?: StateUpdater<{ width: number; height: number }>;
};

/**
 * The internal component used to render a single item in CardGrid.
 */
export function CardGridItem<K extends string | number, D>({
  children,
  itemKey,
  itemIndex,
  isTabbable,
  isFocused,
  isFocusRingVisible,
  isSelected,
  selectionMode,
  initialAnimation,
  setCardDimension
}: Props<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);

  const variantClasses = multiVariantStyles({
    itemFocused: isFocused && isFocusRingVisible ? 'isItemFocused' : 'isNotItemFocused',
    itemInitialOpacity: initialAnimation ? 'isTransparent' : 'isNotTransparent'
  });
  const classes = classNames([ITEM_STYLE_CLASS, styles.itemStyle, variantClasses]);

  // don't bubble Arrow Up/Down/Left/Right events to parent when in isTabbable mode
  // this bubbling causes the outer CollectionFocus ring to change index while in the isTabbable mode.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      isTabbable &&
      (event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight')
    ) {
      event.stopPropagation();
    }
  };

  useLayoutEffect(() => {
    const firstCard = rootRef.current?.firstElementChild as HTMLElement;
    if (setCardDimension && firstCard) {
      setCardDimension({ width: firstCard.offsetWidth, height: firstCard.offsetHeight });
    }
  }, []);

  // todo: issue track in JET-57681, we currently have two useAnimation() because the opacity and
  // slide up/down have different durations, and the current API couldn't handle this case, we will
  // update this once the API is updated

  // animation for slide up/down
  const { nodeRef: nodeRefSlide } = useAnimation<'slideUp' | 'slideDown' | 'none', HTMLDivElement>(
    initialAnimation || 'none',
    animationConfigSlide(itemIndex)
  );
  // animation for opacity change
  const { nodeRef: nodeRefOpacity } = useAnimation<'opacity' | 'none', HTMLDivElement>(
    (initialAnimation && 'opacity') || 'none',
    animationConfigOpacity(itemIndex)
  );

  const itemRefs = useMemo(
    () => mergeRefs(rootRef, nodeRefSlide, nodeRefOpacity),
    // spot an issue when we click the cards during animation is processing, the cards would not finish
    // the current animations and stay as what it is, e.g. leave itself as half transparent.
    // add initialAnimation to the dependency to prevent this issue.
    [rootRef, nodeRefSlide, nodeRefOpacity, initialAnimation]
  );

  return (
    <div
      id={useId()}
      ref={itemRefs}
      class={classes}
      role="gridcell"
      data-oj-key={itemKey}
      onKeyDown={handleKeyDown}
      aria-selected={selectionMode === 'none' ? undefined : isSelected}
      {...(typeof itemKey === 'number' && { 'data-oj-key-type': 'number' })}>
      <TabbableModeContext.Provider value={{ isTabbable }}>
        <FocusTrap isDisabled={!isTabbable} restoreFocusRef={false}>
          {children}
        </FocusTrap>
      </TabbableModeContext.Provider>
    </div>
  );
}

const animationConfigSlide = (
  index: number
): UseAnimationConfig<'slideUp' | 'slideDown' | 'none', HTMLDivElement> => {
  return {
    animationStates: {
      slideUp: {
        from: {
          translateY: '50px'
        },
        to: {
          translateY: '0px'
        },
        options: {
          duration: 300,
          delay: index * 50,
          easing: [0, 0, 0.2, 1]
        }
      },
      slideDown: {
        from: {
          translateY: '-50px'
        },
        to: {
          translateY: '0'
        },
        options: {
          duration: 300,
          delay: index * 50,
          easing: [0, 0, 0.2, 1]
        }
      }
    },
    isAnimatedOnMount: true
  };
};

const animationConfigOpacity = (
  index: number
): UseAnimationConfig<'opacity' | 'none', HTMLDivElement> => {
  return {
    animationStates: {
      opacity: {
        from: {
          opacity: 0
        },
        to: {
          opacity: 1
        },
        options: {
          duration: 150,
          delay: index * 50,
          easing: 'linear'
        }
      }
    },
    isAnimatedOnMount: true
  };
};

export const MemoizeCardGridItem = memo(
  CardGridItem,
  <K, D>(prev: Props<K, D>, next: Props<K, D>) => {
    if (prev && next) {
      return (
        prev.itemKey === next.itemKey &&
        prev.itemIndex === next.itemIndex &&
        prev.itemData === next.itemData &&
        prev.isTabbable === next.isTabbable &&
        prev.isFocused === next.isFocused &&
        prev.isFocusRingVisible === next.isFocusRingVisible &&
        prev.isSelected === next.isSelected &&
        prev.initialAnimation === next.initialAnimation
      );
    }
    return false;
  }
);
